import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  CashCountStyles,
  DENOMINATIONS,
  grossOf,
  money,
  num,
  todayInput,
} from "./CashCountShared";

const emptyCounts = () =>
  DENOMINATIONS.reduce((acc, d) => ({ ...acc, [d.qty]: "" }), {});

const Create = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(todayInput());
  const [counts, setCounts] = useState(emptyCounts);
  const [suspense, setSuspense] = useState("");
  const [error, setError] = useState({});
  const [saving, setSaving] = useState(false);

  const gross = useMemo(() => grossOf(counts), [counts]);
  const netTotal = gross - num(suspense);
  const notesCounted = useMemo(
    () => DENOMINATIONS.reduce((sum, d) => sum + num(counts[d.qty]), 0),
    [counts]
  );

  const handleCountChange = (e) => {
    const { name, value } = e.target;
    setCounts((prev) => ({ ...prev, [name]: value }));
  };

  const resetCounts = () => {
    setCounts(emptyCounts());
    setSuspense("");
  };

  const formSubmit = () => {
    // store() still expects the legacy quantity spellings, so the payload is
    // built from the ladder rather than mirrored straight out of state.
    const payload = { date, suspense: num(suspense), total_amount: netTotal };
    DENOMINATIONS.forEach((d) => {
      payload[d.createQty] = num(counts[d.qty]);
      payload[d.total] = num(counts[d.qty]) * d.value;
    });

    setSaving(true);
    Http.post("/cash-count", payload)
      .then((res) => {
        navigate("/cash-count", {
          state: { message: res.data.message, type: "success" },
        });
      })
      .catch((e) => {
        setSaving(false);
        setError(e.response?.data?.errors || {});
      });
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <CashCountStyles />
        <div className="am-pagetitle">
          <h5 className="am-title">Create Cash Count</h5>
          <Link to="/cash-count" className="btn btn-outline-secondary btn-sm">
            <i className="fa fa-arrow-left mg-r-6"></i>Back to List
          </Link>
        </div>

        <div className="am-pagebody">
          <div className="row">
            <div className="col-lg">
              <FormValidation error={error} />
            </div>
          </div>

          <div className="cc-panel">
            <div className="cc-panel-head">
              <span>
                <i className="fa fa-calendar-o me-2"></i>Count Information
              </span>
            </div>
            <div className="cc-panel-body">
              <div className="row">
                <div className="col-lg-4">
                  <label>
                    Count Date <i className="fa fa-star required"></i>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-8">
              <div className="cc-panel">
                <div className="cc-panel-head">
                  <span>
                    <i className="fa fa-money me-2"></i>Denomination Breakdown
                  </span>
                  <span className="cc-head-note">
                    Enter the number of notes or coins counted for each
                    denomination
                  </span>
                </div>
                <div className="table-responsive">
                  <table className="table cc-count-table">
                    <thead>
                      <tr>
                        <th style={{ width: "22%" }}>Denomination</th>
                        <th style={{ width: "30%" }}>Quantity</th>
                        <th style={{ width: "8%" }}></th>
                        <th className="cc-num" style={{ width: "20%" }}>
                          Face Value
                        </th>
                        <th className="cc-num" style={{ width: "20%" }}>
                          Line Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {DENOMINATIONS.map((d) => {
                        const lineTotal = num(counts[d.qty]) * d.value;
                        return (
                          <tr key={d.qty}>
                            <td>
                              <span className="cc-note">৳ {d.label}</span>
                            </td>
                            <td>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                className="form-control cc-qty-input"
                                name={d.qty}
                                placeholder="0"
                                value={counts[d.qty]}
                                onChange={handleCountChange}
                              />
                            </td>
                            <td className="cc-op text-center">&times;</td>
                            <td className="cc-num">{money(d.value)}</td>
                            <td
                              className={`cc-num cc-line-total ${
                                lineTotal ? "" : "is-zero"
                              }`}
                            >
                              {money(lineTotal)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th colSpan="4" className="cc-num">
                          Gross Cash Counted
                        </th>
                        <th className="cc-num">{money(gross)}</th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="cc-panel">
                <div className="cc-panel-head">
                  <span>
                    <i className="fa fa-calculator me-2"></i>Summary
                  </span>
                </div>
                <div className="cc-panel-body">
                  <label>Suspense Amount</label>
                  <input
                    type="number"
                    step="any"
                    className="form-control cc-num mg-b-4"
                    name="suspense"
                    placeholder="0.00"
                    value={suspense}
                    onChange={(e) => setSuspense(e.target.value)}
                  />
                  <small className="text-muted d-block mg-b-20">
                    Unverified cash held aside. It is deducted from the gross
                    count to give the net total.
                  </small>

                  <div className="cc-total-box">
                    <div className="cc-total-row">
                      <span>Notes &amp; coins counted</span>
                      <span>{notesCounted}</span>
                    </div>
                    <div className="cc-total-row">
                      <span>Gross cash</span>
                      <span>৳ {money(gross)}</span>
                    </div>
                    <div className="cc-total-row">
                      <span>Less: suspense</span>
                      <span>৳ {money(suspense)}</span>
                    </div>
                    <div className="cc-total-row cc-total-net">
                      <span>Net Total</span>
                      <span>৳ {money(netTotal)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={resetCounts}
                  disabled={saving}
                >
                  <i className="fa fa-eraser mg-r-6"></i>Clear
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={formSubmit}
                  disabled={saving}
                >
                  <i className="fa fa-send mg-r-10"></i>
                  {saving ? "Saving..." : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Create;
